import React, { useState, useEffect, useRef } from 'react';
/* 使用 sessionStorage 缓存共享数据，缓存由 shared/cache.ts 管理 */
import SharingApplyItem from '../../components/sharing-apply-item';
import Empty from '../../components/empty';
import SharingRejectModal from '../../components/sharing-reject-modal';
import { CHARGE_CDN, isXPApp } from '../../../../utils/constants';
import {
  queryUseApply,
  queryUseApplyingNo,
  useShareApplyApproval,
  getPileStatus,
  applyUseRefuseReasonEnum,
  querySharePileInfo,
  modifyUseApplyAutoAudit,
  queryShareApplyInfo,
  queryStationDtl
} from '../../api';
import Toast from '@xpeng/rc-toast';
import { trackGdp } from '../../../../utils/gio-report';
import { UserTypeEnum } from '../../../../api/pile';
import { goToPage } from '../../../../utils/common';
/* 复用 pile-sharing/pages/apply/index.tsx 中的 Switch 实现 */
import { Switch, Dialog, Loading } from "@xp-design/mobile";
import './index.less'
import { getOrFetchSharePileInfo, getOrFetchStationDtl } from '../../shared/cache';
import SharingMessageModal from '../../components/sharing-message-modal';


const pageSize = 10;

const ApplyReviewPage = () => {
  const [applyList, setApplyList] = useState<any[]>([]);
  const [applyPageNo, setApplyPageNo] = useState<number>(1);
  const [applyListTotal, setApplyListTotal] = useState<number>(0);
  const [applyNo, setApplyNo] = useState<number>(0);
  const [rejectReasonList, setRejectReasonList] = useState<any[]>([]);
  const [userInfo, setUserInfo] = useState<any>({});
  const [stationId, setStationId] = useState<string | null>(null);
  const [statePileNo, setPileNo] = useState<string | null>(null);
  const [showPopupModel, setShowPopupModel] = useState<boolean>(false);
  // 使用数字 0/1 存储状态以复用参照文件的转换策略
  const [applyNoReviewNumeric, setApplyNoReviewNumeric] = useState<number>(0);
  const [applyNoReviewLoading, setApplyNoReviewLoading] = useState<boolean>(false);
  const [rejectVisible, setRejectVisible] = useState<boolean>(false);
  const [currentApprovalId, setCurrentApprovalId] = useState<string>('')
  const isAgreeingRef = useRef<boolean>(false);
  const [stationData, setStationData] = useState<any>(null);
  const [msgModalData, setMsgModalData] = useState<{
    visible: boolean; text: string; confirmText: string; cancelText: string; withoutCancel?: boolean; title?: string; onConfirm?: () => void; onCancel?: () => void
  }>({
    visible: false,
    text: '',
    cancelText: '',
    confirmText: '',
    withoutCancel: false,
    title: '温馨提示'
  })

  const applyListRef = useRef(null);
  const applyListLoading = useRef(false);

  useEffect(() => {
    document.title = '申请审核';
    init();
  }, []);

  const init = async () => {
    try {
      const data = await getOrFetchSharePileInfo(() => querySharePileInfo());
      if (data) {
        // 如果存在 isUseApplyAutoAudit 字段（可能为 0/1 或 boolean），按要求处理
        setStationId(data.stationId);
        setPileNo(data.pileNo);
        const stationResData = await getOrFetchStationDtl(`${data?.stationId}`, (id) => queryStationDtl({ stationId: id, latitude: 0, longitude: 0 }));
        if (stationResData) {
          setStationData(stationResData)
        }
        getApprovalData(data.stationId);
        getApplyInfo(data.pileNo);
      }
      const rejectReasonRes = await applyUseRefuseReasonEnum();
      if (rejectReasonRes?.data && rejectReasonRes.code === 200) {
        setRejectReasonList(rejectReasonRes.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getApplyInfo = async (pileNo = statePileNo) => {
    try {
      const res = await queryShareApplyInfo({ pileNo });
      if (res?.code === 200 && res?.data) {
        const data = res.data;
        // 如果存在 isUseApplyAutoAudit 字段（可能为 0/1 或 boolean），按要求处理
        setApplyNoReviewNumeric(data.isUseApplyAutoAudit ? 1 : 0);
      }
    } catch (e) {
      console.error('getApplyInfo error', e);
    }
  }

  const onApplyScroll = () => {
    const listDom: any = applyListRef.current;
    if (listDom) {
      const isEnd = listDom.scrollTop + 50 >= listDom.scrollHeight - listDom.clientHeight;
      if (isEnd) {
        if (applyList.length >= applyListTotal) {
          return;
        }
        let nextPage = applyPageNo + 1;
        if (applyPageNo > 1 && (applyPageNo * pageSize > applyList.length)) {
          nextPage = applyPageNo;
        }
        setApplyPageNo(nextPage);
        if (stationId && !applyListLoading.current) {
          getApprovalData(stationId, nextPage);
        }
      }
    }
  };

  const getApprovalData = async (stationIdParam: string, pageNo?: number) => {
    try {
      applyListLoading.current = true;
      const res = await queryUseApply({
        stationId: stationIdParam,
        pageNo: pageNo || 1,
        pageSize: pageSize,
        dataScope: 2
      });
      const applyingRes = (pageNo && pageNo !== 1) ? { data: { records: [] } } : await queryUseApply({ stationId: stationIdParam, pageNo: 1, pageSize: 100, dataScope: 1 });
      const applyNoRes = await queryUseApplyingNo({ stationId: stationIdParam });

      if (res?.code === 200) {
        setApplyList((pageNo && pageNo !== 1) ? applyList.concat(res?.data?.records) : (applyingRes?.data?.records || []).concat(res?.data?.records));
        setApplyListTotal(res?.data?.total);
      }
      if (applyNoRes?.data && applyNoRes?.code === 200) {
        setApplyNo(applyNoRes?.data?.applyingNo);
      }
    } catch (error) {
      console.log('getApprovalData error', error);
    } finally {
      applyListLoading.current = false;
    }
  };

  // 辅助函数：调用 modifyUseApplyAutoAudit 接口并返回操作结果（不在此修改组件状态）
  const callModifyAutoAudit = async (value: number) => {
    if (!stationId) {
      Toast.show('站点信息缺失，操作失败');
      return { success: false };
    }
    const hide = Loading.show();
    try {
      setApplyNoReviewLoading(true);
      // 使用后端期望的字段名 useApplyAutoAudit，若类型不匹配可断言为 any
      const res = await modifyUseApplyAutoAudit({ stationId, isUseApplyAutoAudit: value } as any);
      if (res?.code === 200) {
        return { success: true, res };
      } else {
        Toast.show(value === 1 ? '开启申请免审核失败' : '关闭申请免审核失败');
        return { success: false, res };
      }
    } catch (e) {
      // Toast.show('网络或服务器错误，请稍后重试');
      return { success: false };
    } finally {
      hide()
      setApplyNoReviewLoading(false);
    }
  };

  const reject = (id: string) => {
    // 上报埋点并通过事件告知外部使用 SharingRejectModal（home 页面有 modal，可监听此事件）
    trackGdp('P20075', 'B016', {
      userId: userInfo?.uid,
      userType: UserTypeEnum[userInfo?.type]
    });
    setRejectVisible(true);
    setCurrentApprovalId(id);
  };

  const agree = async (data: any) => {
    if (isAgreeingRef.current) {
      return;
    }

    isAgreeingRef.current = true;

    try {
      let res = await useShareApplyApproval({
        applyReqId: data?.applyReqId,
        approvalResult: 2,
      });

      await new Promise((resolve) => setTimeout(resolve, 3000));
      if (res?.code === 200) {
        Toast.show('同意申请成功');
        await getApprovalData(`${stationId}`);
        let res2 = await getPileStatus({ pileNo: data.pileNo });
        if (res2?.code === 200) {
          if (res2?.data?.isOpenLockMode !== 1) {
            setMsgModalData({
              visible: true,
              confirmText: '去开启',
              cancelText: '取消',
              text: '已通过对方申请，你的家充当前处于未上锁状态，存在被盗风险，建议开启防盗锁',
              title: '提示',
              onConfirm: () => {
                if (isXPApp) {
                  goToPage('xiaopeng://account/charging/my')
                } else {
                  Toast.show('请在小鹏APP内进行操作', {
                    containerStyle: {
                      zIndex: 9999
                    }
                  })
                }
              }
            })
          }
        }
      } else {
        Toast.show(res.msg || '同意申请失败');
      }
    } catch (error) {
      console.error('同意申请出错', error);
      Toast.show('同意申请失败');
    } finally {
      isAgreeingRef.current = false;
    }
  };



  const goToUserList = () => {
    const { origin, pathname } = window.location;
    goToPage(`${origin}${pathname}#/user-list?stationId=${stationId}`);
  };

  const refreshApprovalList = async () => {
    stationId && await getApprovalData(stationId)
  }

  const isCloseShare = stationData?.operationDto?.stationStatus === '03';

  return (
    <div className='apply-review-page'>
      <div className='apply-list' onScroll={onApplyScroll} ref={applyListRef}>
        <div className='user-list-row' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className='user-list' onClick={goToUserList} >
            用户列表
            <img className='more-icon' src={`${CHARGE_CDN}/common/arrow-right-normal.png`} alt='' />
          </div>
          {
            stationData?.operationDto && stationData?.operationDto?.stationStatus !== '03'
              ? <div className='user-list-right-switch' onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="apply-no-review-label">申请免审核</div>
                  <Switch
                    checked={(applyNoReviewNumeric ? applyNoReviewNumeric : 0) === 1}
                    disabled={applyNoReviewLoading}
                    onChange={async (checked) => {
                      if (checked) {
                        trackGdp('P20287', 'B001')
                        Dialog.confirm({
                          title: '是否开启申请免审核',
                          content: <>
                            开启后新的共享家充使用申请将自动通过，请注意以下内容：<br />
                            1. 申请自动通过后，你将收到即时的通知提醒<br />
                            2. 申请自动通过与你主动同意的效果完全一致，对方可获得你的联系方式，并能在合适的时间付费使用你的家充桩<br />
                            3. 你可以随时关闭申请免审核功能，也能在用户列表中移除经系统自动通过的用户<br />
                          </>,
                          confirmText: '开启',
                          cancelText: '暂不开启',
                        }).then(async (ok: boolean) => {
                          if (ok) {
                            trackGdp('P20287', 'B002')
                            // 用户在确认弹窗中确认后的逻辑
                            const result = await callModifyAutoAudit(1);
                            if (result?.success) {
                              getApplyInfo()
                              setApplyNoReviewNumeric(1);
                              Toast.show('开启申请免审核成功');
                            }
                          } else {
                            // 取消时保持开关为关闭状态
                            setApplyNoReviewNumeric(0);
                          }
                        });
                      } else {
                        // 关闭时直接调用接口
                        const result = await callModifyAutoAudit(0);
                        if (result?.success) {
                          getApplyInfo()
                          setApplyNoReviewNumeric(0);
                          Toast.show('关闭申请免审核成功');
                        }
                      }
                    }}
                    style={{
                      '--width': '10.66666vw',
                      '--height': '5.86666vw',
                    }}
                  />
                </div>
              : null
          }
        </div>
        {
          applyList.length
            ?
            applyList.map((item, index) => {
              return <SharingApplyItem
                data={item}
                key={index}
                reject={reject}
                agree={agree}
                rejectReason={rejectReasonList}
              />
            })
            :
            <div className='apply-list-empty'>
              <Empty text='邀请别人来充电，共享低碳生活' />
            </div>
        }
      </div>
      {/* {showPopupModel && (
        <OrdinaryModel
          type={2}
          content="是否开启申请免审核"
          tip={
          }
          cancelText={'暂不开启'}
          cancelCbk={() => {
            setShowPopupModel(false);
            setApplyNoReviewNumeric(0);
          }}
          confirmText={'开启'}
          confirmCbk={() => {
            setShowPopupModel(false);
            setApplyNoReviewNumeric(1);
          }}
        />
      )} */}
      <SharingRejectModal
        visible={rejectVisible}
        setVisible={(status) => setRejectVisible(status)}
        refreshList={refreshApprovalList}
        approvalId={currentApprovalId}
        userInfo={userInfo}
      />
      <SharingMessageModal
        visible={msgModalData.visible}
        confirmText={msgModalData.confirmText}
        cancelText={msgModalData.cancelText}
        onCancel={msgModalData?.onCancel ? msgModalData?.onCancel : () => setMsgModalData({ ...msgModalData, visible: false })}
        onConfirm={msgModalData?.onConfirm ? msgModalData?.onConfirm : () => setMsgModalData({ ...msgModalData, visible: false })}
        text={msgModalData.text}
        withoutCancel={msgModalData.withoutCancel}
        title={msgModalData.title}
      />
    </div>
  );
};

export default ApplyReviewPage;